import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectTemplate } from './models';
import { baseApi } from '@centerpiece/components';

export const project_template = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectTemplates', 'ProjectTemplate'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectTemplates: builder.query<
        ProjectTemplate[],
        { params?: object }
      >({
        query: ({ params }: { params?: object }) => ({
          url: 'project_template',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectTemplate' as const,
                  id,
                })),
                'ProjectTemplates',
              ]
            : ['ProjectTemplates'],
      }),
      getProjectTemplate: builder.query<ProjectTemplate, string>({
        query: id => ({
          url: `project_template/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectTemplate', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectTemplate: builder.mutation({
        query: request => ({
          url: 'project_template',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectTemplates'],
      }),
      updateProjectTemplate: builder.mutation<ProjectTemplate, ProjectTemplate>(
        {
          query: request => ({
            url: `project_template/${request.id}`,
            method: 'PUT',
            body: request,
          }),
          invalidatesTags: (result, error, arg) => [
            { type: 'ProjectTemplate', id: arg.id },
          ],
          async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
            const patchResult = dispatch(
              project_template.util.updateQueryData(
                'getProjectTemplate',
                `${id}`,
                draft => {
                  Object.assign(draft, patch);
                }
              )
            );
            try {
              await queryFulfilled;
            } catch {
              patchResult.undo();

              /**
               * Alternatively, on failure you can invalidate the corresponding cache tags
               * to trigger a re-fetch:
               * dispatch(project_template.util.invalidateTags(['ProjectTemplates']))
               */
              dispatch(
                project_template.util.invalidateTags(['ProjectTemplates'])
              );
            }
          },
        }
      ),
      deleteProjectTemplate: builder.mutation<ProjectTemplate, number>({
        query: id => ({
          url: `project_template/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectTemplate' as const, arg }, 'ProjectTemplates']
            : ['ProjectTemplates'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectTemplatesQuery,
  useGetProjectTemplateQuery,
  useCreateProjectTemplateMutation,
  useUpdateProjectTemplateMutation,
  useDeleteProjectTemplateMutation,
  usePrefetch: useProjectTemplatesPrefetch,
} = project_template;
const selectProjectTemplatesRoot =
  project_template.endpoints.getProjectTemplates.select({});
export const selectProjectTemplates = createSelector(
  selectProjectTemplatesRoot,
  project_template => project_template.data || []
);
